import React from 'react';
import metricsDoc from '~/data/metrics_documentation.json';
import type { MetricsDocumentation } from '~/types/metrics';

/**
 * Maps widget label to metric key in the documentation
 */
const labelToMetricKey: Record<string, keyof MetricsDocumentation['metrics']> =
  {
    // Expected Impact Widget labels
    'Expected Annual Return (EWMA)': 'expected_annual_return_ewma',
    'Annual Risk (Ledoit-Wolf)': 'annual_risk_ledoit_wolf',
    'Ex-Ante CVaR (95%, 21-day)': 'cvar_ex_ante',
    'Shortfall Probability (vs 0%)': 'shortfall_probability',
    'Forward Max Drawdown Forecast': 'max_drawdown',
    'Drawdown Budget Utilization': 'drawdown_budget_utilization',
    'Portfolio Beta': 'beta_ex_ante',
    'Tracking Error': 'tracking_error_ex_ante',
    // Fallback labels
    'Expected Annual Return Improvement': 'expected_annual_return_ewma',
    'Risk Reduction': 'annual_risk_ledoit_wolf',
    // Proposed Portfolio Widget labels
    'Max Drawdown': 'max_drawdown',
    'Sortino Ratio': 'sortino_ratio',
    'FX Volatility': 'fx_volatility',
    Beta: 'beta_ex_ante',
    'Forward Max Drawdown': 'max_drawdown',
    'Drawdown Budget': 'drawdown_budget',
    'Cash Allocation': 'cash_allocation',
    'Total Allocation': 'total_allocation',
    // Market Analysis Widget labels
    'Sector Momentum': 'sector_momentum',
    'Avg P/E Ratio': 'avg_portfolio_pe',
    'News Sentiment': 'news_sentiment',
    'Volatility Index': 'volatility_index',
  };

/**
 * Gets the description for a metric label from the documentation
 */
export const getMetricDescription = (label: string): string | undefined => {
  const metricKey = labelToMetricKey[label];
  if (!metricKey) {
    return undefined;
  }

  const doc = metricsDoc as MetricsDocumentation;
  const metric = doc.metrics[metricKey];
  return metric?.description;
};

/**
 * Gets metric data including description and formula
 */
export const getMetricData = (
  label: string,
):
  | {
      description: string;
      formula_plain?: string;
    }
  | undefined => {
  const metricKey = labelToMetricKey[label];
  if (!metricKey) {
    return undefined;
  }

  const doc = metricsDoc as MetricsDocumentation;
  const metric = doc.metrics[metricKey];
  if (!metric) {
    return undefined;
  }

  return {
    description: metric.description,
    formula_plain: metric.formula_plain,
  };
};

/**
 * Gets the formatted description with formula for a metric label
 * Returns a string that can be used directly, or use getMetricData for structured data
 */
export const getMetricDescriptionWithFormula = (
  label: string,
): string | React.ReactNode | undefined => {
  const metricData = getMetricData(label);
  if (!metricData) {
    return undefined;
  }

  // Return structured format that MetricLabel can render
  if (metricData.formula_plain) {
    // Return as React element - this will be handled by MetricLabel which accepts ReactNode
    return React.createElement(
      React.Fragment,
      null,
      React.createElement('div', { className: 'mb-2' }, metricData.description),
      React.createElement(
        'div',
        {
          className: 'mt-2 pt-2 border-top-1',
          style: { borderColor: 'var(--bq-tooltip-border)' },
        },
        React.createElement(
          'div',
          {
            className: 'text-xs mb-1 font-semibold',
            style: { color: 'var(--bq-text-tertiary)' },
          },
          'Formula:',
        ),
        React.createElement(
          'div',
          {
            className: 'text-xs font-mono',
            style: { color: 'var(--bq-tooltip-text)' },
          },
          metricData.formula_plain,
        ),
      ),
    );
  }

  return metricData.description;
};
